import React, { useEffect, useState } from "react";
import * as dateFns from "date-fns";
import { Dropdown } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";

import TimeSelector from "../Shared/TimeSelector";
import PeriodsList from "../Shared/Periods/PeriodsList";
import {
  CONFIRMATION_MODAL_CONFIG,
  DEFAULT_PROFILE_VENUE_STATE,
  FILTER_GLOBAL_KEYS,
  LEAGUE,
  NCAA_FBS_GAME,
  NFL_GAME,
  PRACTICE,
} from "../../config/constants";
import {
  buildIntensityFilter,
  findVenueOptionFilter,
  getTeamData,
  getTeamProps,
  getTeamsDropdownLabel,
  getVenues,
  isBlankString,
  isCC,
  isNCAAAllSports,
  text_truncate,
} from "../../helpers";
import ConfirmationModal from "../Shared/Modals/ConfirmationModal";
import {
  selectIntensitiesByTeam,
  useFiltersState,
  useProfilesDispatch,
  useProfilesState,
} from "../../store/hooks";
import { LeagueValue, ProfileModalComponent } from "./types";
import { Drill, TeamData } from "../../config/commonTypes";
import { VenueSelectorData } from "../Shared/Filters/VenueSelector/types";
import { VenueType } from "../../store/types/filters";
import { SimpleMultiSelectorPresentational } from "components/Shared/Filters/SimpleMultiSelector";
import { useSelector } from "react-redux";

const NewProfileModalComponent = ({
  show,
  onHide,
  createMode,
  selectedProfile,
  selectedTeam,
  selectedLeague,
}: ProfileModalComponent) => {
  const { user } = useProfilesState();
  const { createProfile, updateProfile, deleteProfile } = useProfilesDispatch();

  const [profile, setProfile] = useState(
    JSON.parse(JSON.stringify(selectedProfile))
  );
  const [intensityArray, setIntensityArray] = useState<string[]>([]);
  const close = () => setShowFilter(-1);
  const [showFilter, setShowFilter] = useState(0);
  const [periods, setPeriods] = useState<Drill[]>([]);
  const league = isNCAAAllSports(user)
    ? { label: "NCAA-FBS Practice", value: "ncaa-fbs" }
    : selectedLeague;
  const [modalShow, setModalShow] = useState(false);
  const [venue, setVenue] = useState<VenueType & TeamData>(
    DEFAULT_PROFILE_VENUE_STATE as VenueType & TeamData
  );
  const { [FILTER_GLOBAL_KEYS.VENUES]: venues } = useFiltersState(
    "_",
    FILTER_GLOBAL_KEYS.VENUES
  );
  const intensities = useSelector(
    selectIntensitiesByTeam(selectedTeam ? selectedTeam : user.teamId)
  );
  const intensityFilter = buildIntensityFilter(intensities ?? []);

  const currentTypes = (selectedProfile?.intensity ?? '')
    .split(',')
    .map(type => type.trim())
    .filter(Boolean)
    .map(type => ({ value: type, name: type }));

  const missingTypes = currentTypes.filter(
    selItem => !intensityFilter.types.some(t => t.value === selItem.value)
  );

  const updatedIntensityFilter = {
    ...intensityFilter,
    types: [...missingTypes, ...intensityFilter.types],
  };
  useEffect(() => {
    // Get the profile venue and league on "Edit" btn click.
    if (
      selectedProfile &&
      selectedProfile.league &&
      selectedProfile.siteId !== venue.siteId
    ) {
      const { league, type, teamId } = selectedProfile;
      const { value } = (findVenueOptionFilter(venues, league, type, teamId) ||
        {}) as VenueSelectorData;
      const teamData = (getTeamData(teamId, league) || {}) as TeamData;
      setVenue({ ...value, ...teamData });
    }
  }, [selectedProfile]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value !== " ")
      setProfile({ ...profile, profileName: e.target.value });
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfile({ ...profile, description: e.target.value });
  };

  const handleIntensityChange = (name: string[]) => {
    let intensities = "";
    name.forEach((value, index) => {
      intensities = index === 0 ? value : intensities + "," + value;
    });

    setProfile({ ...profile, intensity: intensities });
  };

  const handleTeamChange = (venue: VenueType) => {
    const teamData = (getTeamData(venue.teamId as string, venue.league) ||
      {}) as TeamData;
    setVenue({ ...venue, ...teamData });
    setProfile({ ...profile, teamId: venue.teamId, siteId: venue.teamId });
  };

  const handleLeagueChange = (league: LeagueValue) => {
    setProfile({ ...profile, teamId: "", league: league.value });
    setVenue(DEFAULT_PROFILE_VENUE_STATE as VenueType & TeamData);
    //setLeague(league);
  };

  const time =
    profile && profile.startTime
      ? dateFns.toDate(profile.startTime)
      : dateFns.toDate(new Date());

  const getInitialProfile = () => {
    return {
      profileName: "",
      description: "",
      last_modified: new Date().getTime(),
      drills: [],
      intensity: "",
      siteId: isCC(user) || isNCAAAllSports(user) ? selectedTeam : user.teamId, //TO DO CHECK USER
      teamId: isCC(user) || isNCAAAllSports(user) ? selectedTeam : user.teamId,
      startTime: dateFns.toDate(new Date()).getTime(),
      type: "practice",
      league: isCC(user)
        ? selectedLeague.value === "ncaa"? "ncaa-fbs" : selectedLeague.value
        : isNCAAAllSports(user)
        ? "ncaa-fbs"
        : user.league,
      created_by: user.givenName,
    };
  };

  const onEntering = () => {
    const newSelectedProfile = selectedProfile
      ? selectedProfile
      : getInitialProfile();
    if (!newSelectedProfile.league) {
      const teamData = getTeamProps(selectedProfile.teamId) || ({} as TeamData);
      if (teamData) {
        newSelectedProfile.league = teamData!.league;
      } else {
        newSelectedProfile.league = "nfl";
      }
    }
    if (
      selectedTeam &&
      selectedLeague &&
      (isCC(user) || isNCAAAllSports(user))
    ) {
      const teamData = (getTeamData(
        selectedTeam as string,
        selectedLeague.value
      ) || {}) as TeamData;
      setVenue({ ...venue, ...teamData });
    }
    setProfile(newSelectedProfile);
    setPeriods(
      selectedProfile ? JSON.parse(JSON.stringify(selectedProfile.drills)) : []
    );
    setIntensityArray(
      selectedProfile
        ? typeof JSON.parse(JSON.stringify(selectedProfile.intensity)) ===
          "string"
          ? selectedProfile?.intensity?.split(",").map((type) => type)
          : JSON.parse(JSON.stringify(selectedProfile.intensity))
        : []
    );
  };

  const onApplyTimeSelector = (
    hour: number,
    minute: number,
    second: number,
    am: boolean
  ) => {
    if (am === true) {
      if (hour === 12) {
        hour = 0;
      }
    } else {
      if (hour !== 12) {
        hour += 12;
      }
    }

    let newTime = time;
    newTime = dateFns.setHours(newTime, hour);
    newTime = dateFns.setMinutes(newTime, minute);
    newTime = dateFns.setSeconds(newTime, second);
    setProfile({ ...profile, startTime: newTime.getTime() });
  };

  // let time = profile ? toDate(profile.last_modified) : toDate(new Date());

  const modalTitle = createMode
    ? "Create Practice Profile"
    : "Edit Practice Profile";
  const leagueItems: Array<JSX.Element> = [];
  LEAGUE.types.forEach((type, index) => {
    if (type.value.label !== NCAA_FBS_GAME && type.value.label !== NFL_GAME) {
      leagueItems.push(
        <Dropdown.Item
          key={"league" + index}
          onClick={() => handleLeagueChange(type.value)}
        >
          {type.value.label}
        </Dropdown.Item>
      );
    }
  });
  /* let leagueItems = LEAGUE.types.map((type, index) => (
   <Dropdown.Item key={'league' + index}
   onClick={() => handleLeagueChange(type.value)}>
   {type.value.label}
   </Dropdown.Item>)); */
  let teamItems = [];
  if (!isNCAAAllSports(user)) {
    teamItems = getVenues(venues, league.value, PRACTICE).map(
      (venue: VenueType, index: number) => {
        return (
          <Dropdown.Item
            key={"team" + index}
            onClick={() => handleTeamChange(venue)}
          >
            {`(${venue.teamId}) ${venue.siteName}`}
          </Dropdown.Item>
        );
      }
    );
  } else {
    teamItems = [
      ...getVenues(venues, "ncaa-fbs", PRACTICE),
      ...getVenues(venues, "ncaa-mlax", PRACTICE),
    ].map((venue, index) => {
      return (
        <Dropdown.Item
          key={"team" + index}
          onClick={() => {
            handleTeamChange(venue);
          }}
        >
          {venue.league === "ncaa-fbs" || venue.league === "ncaa-mlax"
            ? `${venue.siteCityAbbr.toUpperCase()} ${
                venue.league === "ncaa-mlax" ? "MLAX" : "FB"
              }`
            : `(${venue.teamId}) ${venue.siteName}`}
        </Dropdown.Item>
      );
    });
  }

  const deleteProfileContent = {
    headerContent: " ",
    bodyContent: "Are you sure you want to delete this profile?",
    confirmContent: "Yes, delete profile",
  };
  const disabled = profile
    ? isBlankString(profile.profileName) ||
      isBlankString(profile.teamId) ||
      isBlankString(profile.siteId) ||
      periods.length === 0
    : true;
  return (
    <>
      <ConfirmationModal
        show={modalShow}
        onHide={() => {
          setModalShow(false);
          onHide();
        }}
        modalConfig={CONFIRMATION_MODAL_CONFIG}
        modalContent={deleteProfileContent}
        confirmAction={() => deleteProfile(profile)}
      />
      <Modal
        className="modal-content-02"
        show={show}
        onHide={onHide}
        onEntering={() => onEntering()}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        dialogClassName={isCC(user) ? "cc-profile-modal" : ""}
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            <div className="m-profil-h">{modalTitle}</div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="m-profile-b">
            <div className="material-textbox" id="first-name-input">
              <input
                className="material-input"
                required={profile ? profile.profileName === "" : true}
                type="text"
                value={profile ? profile.profileName : ""}
                onChange={(e) => handleNameChange(e)}
              />
              <span className="material-label">Enter a profile name</span>
            </div>
            <div className="material-textbox" id="first-name-input">
              <input
                className="material-input"
                type="text"
                required
                style={{ backgroundImage: "none" }}
                value={profile ? profile.description : ""}
                onChange={(e) => handleDescriptionChange(e)}
              />
              <span className="material-label">
                Enter a profile description
              </span>
            </div>

            {isCC(user) && (
              <div
                className="m-profile-t"
                style={{ opacity: "0.5", pointerEvents: "none" }}
              >
                <div className="d-profile-t">
                  <p>League</p>
                  <Dropdown>
                    <Dropdown.Toggle
                      id="dropdown-basic"
                      className="btn-gray-l"
                      disabled={!createMode}
                    >
                      {/*Conditionally renders Select or provided intensity string*/}
                      {!league.label
                        ? "Select"
                        : league.label === ""
                        ? "Select"
                        : text_truncate(league.label, 15)}
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ height: "fit-content" }}>
                      {leagueItems}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <div className="d-profile-t">
                  <p>Team</p>
                  <Dropdown>
                    <Dropdown.Toggle id="dropdown-basic" className="btn-gray-l">
                      {/*Conditionally renders Select or provided intensity string*/}
                      {getTeamsDropdownLabel(profile && profile.teamId, venue)}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>{teamItems}</Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            )}

            <div className="m-profile-t">
              {isNCAAAllSports(user) && (
                <div
                  className="d-profile-t"
                  style={{
                    opacity: "0.5",
                    pointerEvents: "none",
                    marginRight: "30px",
                  }}
                >
                  <p>Team</p>
                  <Dropdown>
                    <Dropdown.Toggle
                      id="dropdown-basic"
                      style={{ width: "145px" }}
                      className="btn-gray-l"
                    >
                      {/*Conditionally renders Select or provided intensity string*/}
                      {getTeamsDropdownLabel(profile && profile.teamId, venue)}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>{teamItems}</Dropdown.Menu>
                  </Dropdown>
                </div>
              )}
              <div className="d-profile-t" style={{ marginRight: "5px" }}>
                <p>Start Time</p>
                {/* <input type="text" value={time} readOnly={true} /> */}
                <TimeSelector time={time} applyChanges={onApplyTimeSelector} />
              </div>
              <div className="d-profile-t">
                <p>Intensity</p>
                <SimpleMultiSelectorPresentational
                  show={showFilter === 3}
                  close={close}
                  open={() => setShowFilter(3)}
                  filterData={
                    !profile ||
                    profile === "" ||
                    (intensityArray.length === 1 && intensityArray[0] === "")
                      ? []
                      : intensityArray.map((type) => ({
                          value: type,
                          name: type,
                        }))
                  }
                  changeFilterData={(values) => {
                    setIntensityArray(values.map((val) => val.value));
                    handleIntensityChange(values.map((val) => val.value));
                  }}
                  type={updatedIntensityFilter}
                  forceScope={"INTENSITY_DROPDOWN"}
                />
              </div>
            </div>

            <label className="s-pp" />
            <div className="periods-body" style={{ position: "relative" }}>
              <PeriodsList
                profileFlag
                league={league.value}
                periods={periods}
                setPeriods={setPeriods}
                teamId={selectedTeam ? selectedTeam : user.teamId}
              />
            </div>
          </div>
          {createMode ? null : (
            <button
              className="btn-gray btn-hho"
              onClick={() => {
                setModalShow(true);
              }}
            >
              Delete
            </button>
          )}
          <div className="profile-btn">
            <button className="btn-transparet" onClick={onHide}>
              Cancel
            </button>
            <button
              className={createMode ? "btn-blue" : "btn-green"}
              disabled={disabled}
              style={disabled ? { opacity: 0.6 } : {}}
              onClick={() => {
                if (createMode) {
                  createProfile(profile, periods);
                } else {
                  updateProfile(profile, periods);
                }
                onHide();
              }}
            >
              {createMode ? "Create" : "Save"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
export default NewProfileModalComponent;
